import * as yup from "yup";

export const travelStyleCategories = [
  "business",
  "digital-nomad",
  "family-friendly",
  "romantic-getaway",
  "senior",
  "solo",
  "travelling-with-friends",
];

export const accommodationCategories = [
  "hotel",
  "hostel",
  "apartment-or-house",
  "villa",
  "resort",
  "homestay",
];

export const travelActivityCategories = [
  "arts-and-culture",
  "family-activities",
  "festivals-and-events",
  "gastronomy",
  "nightlife-and-entertainment",
  "outdoor-adventures",
  "sightseeing-and-tours",
  "volunteering-and-social-impact",
  "uncategorized",
];

const oneOfTest = (value, validValues) => {
  const isOneOf =
    Array.isArray(value) && value.every((item) => validValues.includes(item));

  return isOneOf;
};

const formatErrorValue = (value) => {
  const humanReadableValue = String(value)
    ?.replaceAll("-", " ")
    .replaceAll("_", " ");

  //   const titleCaseValue =
  //     humanReadableValue.slice(0, 1).toUpperCase() +
  //     humanReadableValue.slice(1, value.length);

  const formattedValue = humanReadableValue;

  return formattedValue;
};

const customOneOfError = (value, validValues) => {
  console.log("value: ", value);
  if (!value) return;

  let valueArray = Array.isArray(value?.value)
    ? value?.value
    : value?.value?.split(", ");

  if (value?.values && valueArray.length === 0) {
    valueArray = value?.values?.split(",");
  }

  const invalidValues = Array.isArray(validValues)
    ? valueArray?.filter((item) => !validValues.includes(item))
    : [];

  // if (invalidValues?.length) {
  //   return "Something went wrong during validation";
  // }

  const formattedValues =
    invalidValues?.map((item) => `"${formatErrorValue(item)}"`) ?? [];

  const valuesLength = formattedValues?.length;

  let formattedValuesString = [...formattedValues].join(`, `);

  if (valuesLength === 2) {
    formattedValuesString = [...formattedValues].join(` and `);
  }

  if (valuesLength > 2) {
    formattedValuesString =
      [...formattedValues].slice(0, valuesLength - 1).join(`, `) +
      `, and ` +
      formattedValues[valuesLength - 1];
  }

  return `${formattedValuesString} ${valuesLength > 1 ? "are" : "is"} not ${
    valuesLength > 1 ? "" : "a"
  } valid option${valuesLength > 1 ? "s" : ""}`;
};

const customStringError = (value) => {
  const formattedValue = formatErrorValue(value?.value);
  let errorMessage = formattedValue
    ? `${formattedValue} is not a valid option`
    : "Required field is missing--please ensure everything is filled in correctly";

  return errorMessage;
};

const customRequiredError = (value) => {
  const formattedValue = formatErrorValue(value?.path);

  return `Please select a ${formattedValue}`;
};

yup.setLocale({
  string: {
    required: (value) => customRequiredError(value),
  },
});

const guideSchemaParts = {
  location: yup
    .object()
    .shape({
      divisions: yup.object().required(),
      feature_types: yup.array().of(yup.string()).nullable(),
      formatted_place: yup.string().required(),
      geometry: yup.object().shape({
        bounding_box: yup
          .object()
          .shape({
            northEast: yup
              .object()
              .shape({
                latitude: yup.number().required(),
                longitude: yup.number().required(),
              })
              .required(),
            southWest: yup
              .object()
              .shape({
                latitude: yup.number().required(),
                longitude: yup.number().required(),
              })
              .required(),
          })
          .required(),
        coordinates: yup.object().shape({
          latitude: yup.number().required(),
          longitude: yup.number().required(),
        }),
      }),
      google_maps_url: yup.string().nullable(),
      name: yup.string().required(),
      short_formatted_place: yup.string().nullable(),
    })
    .nonNullable((value) => customRequiredError(value))
    .defined((value) => customRequiredError(value))
    .test({
      name: "is-defined",
      message: "Please select a location",
      test: (value) => {
        return (
          !!value?.name &&
          !!value?.formatted_place &&
          !!value?.geometry?.coordinates?.latitude
        );
      },
    }),

  dates: yup
    .mixed()
    .nullable()
    .test({
      name: "trip_length",
      message: "Please enter two valid dates",
      test: function (value) {
        if (value === "None" || value === null) return true;

        if (!value) {
          return true;
        }

        let datesObject = value;

        if (typeof value === "string") {
          const split = value.split(" - ");
          datesObject = {
            from: split[0],
            to: split[1],
          };
        }

        const { from, to } = datesObject;

        const dateSchema = yup.date().nullable() || yup.string();

        if (dateSchema.validateSync(from) && dateSchema.validateSync(to)) {
          return true;
        }

        return false;
      },
    }),
  travel_style: yup
    .string()
    .oneOf(travelStyleCategories, (value) =>
      customOneOfError(value, travelStyleCategories)
    )
    .nullable(),
  budget: yup
    .string()
    .oneOf(["low_budget", "average_budget", "high_budget", "luxury_budget"])
    .nullable(),
  accommodation: yup.mixed().test({
    name: "is-allowed-accommodation",
    message: (value) => customOneOfError(value, accommodationCategories),
    test: function (value) {
      return (
        value == false ||
        value == undefined ||
        value == null ||
        oneOfTest(value, accommodationCategories)
      );
    },
  }),
  activities: yup.mixed().test({
    name: "is-allowed-activity",
    message: (value) => customOneOfError(value, travelActivityCategories),
    test: function (value) {
      return (
        value === false ||
        value == false ||
        value == undefined ||
        value == null ||
        oneOfTest(value, travelActivityCategories)
      );
    },
  }),
  author: yup.object().shape({
    id: yup.string(),
    tempUserId: yup.string(),
    name: yup.string(),
    firstName: yup.string(),
    lastName: yup.string(),
    username: yup.string(),
    emailAddress: yup.string().email(),
  }),
};

export const createGuideSchema = yup.object().shape({
  location: guideSchemaParts.location,
  trip_length: guideSchemaParts.dates,
  travel_style: guideSchemaParts.travel_style,
  budget: guideSchemaParts.budget,
  accommodation: guideSchemaParts.accommodation,
  activities: guideSchemaParts.activities,
});

export const guideRequestSchema = yup.object().shape({
  location: guideSchemaParts.location,
  tripData: yup.object().shape({
    dates: yup.lazy((value) =>
      value === "None" || value === null
        ? yup.mixed().oneOf(["None", null]).nullable()
        : guideSchemaParts.dates
    ),
  }),
  preferences: yup.object().shape({
    budget: guideSchemaParts.budget,
    travel_style: guideSchemaParts.travel_style,
    accommodation: guideSchemaParts.accommodation,
    activities: guideSchemaParts.activities,
  }),
  // travel_style: guideSchemaParts.travel_style,
  // accommodation: guideSchemaParts.accommodation,
  // activities: guideSchemaParts.activities,
  author: guideSchemaParts.author,
});
